import { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/Button';

interface Props {
  /** The pack's own override as a 0-1 share; null = follows the platform default. */
  value: number | null;
  /** limits.default_buyback_percent, shown as the placeholder. */
  defaultShare: number;
  disabled: boolean;
  saving: boolean;
  /** A share for an override, null to clear it back to the default. */
  onSave: (share: number | null) => void;
}

const PERCENT_STEP = 0.1;

function toDraft(value: number | null): string {
  return value === null ? '' : String(Math.round(value * 1000) / 10);
}

function parsePercent(draft: string): number | null | 'invalid' {
  const trimmed = draft.trim();
  if (trimmed === '') return null;
  const percent = Number(trimmed);
  if (!Number.isFinite(percent) || percent < 0 || percent > 100) return 'invalid';
  return Math.round(percent * 10) / 1000;
}

/**
 * What the pack pays a player who sells a pulled card back, as a share of the
 * card's value. It lives on the per-operator config row, not on the pack, and
 * it is saved on its own: unlike price, RTP and buckets it stays editable
 * while the pack is live — it prices a card the player already holds and
 * touches neither the solver nor the EV.
 *
 * Empty means "no override": the pack follows the platform default, which the
 * placeholder names rather than pretending to be a value the operator set.
 */
export function BuybackField({ value, defaultShare, disabled, saving, onSave }: Props) {
  const [draft, setDraft] = useState(toDraft(value));

  useEffect(() => {
    setDraft(toDraft(value));
  }, [value]);

  const parsed = parsePercent(draft);
  const invalid = parsed === 'invalid';
  const changed = !invalid && parsed !== value;

  return (
    // Capped width so the explanatory line wraps under the field instead of
    // stretching the row it shares with Price and Target EV.
    <div className="flex max-w-[260px] flex-col gap-1">
      <span className="text-[11px] text-[var(--co-muted)]">Buyback (%)</span>
      <div className="flex items-center gap-2">
        <input
          type="number"
          inputMode="decimal"
          aria-label="Buyback percent"
          aria-invalid={invalid}
          value={draft}
          min={0}
          max={100}
          step={PERCENT_STEP}
          disabled={disabled}
          placeholder={String(Math.round(defaultShare * 1000) / 10)}
          onChange={(e) => setDraft(e.target.value)}
          className={`w-24 rounded border bg-[var(--co-surface)] px-2 py-1 text-right font-mono text-[12px] text-[var(--co-text)] outline-none focus:border-[var(--co-brand)] disabled:opacity-50 ${
            invalid ? 'border-[var(--co-error)]' : 'border-[var(--co-border)]'
          }`}
        />
        <Button
          variant="ghost"
          onClick={() => onSave(parsed === 'invalid' ? null : parsed)}
          disabled={disabled || saving || invalid || !changed}
          className="px-3 py-1 text-[12px]"
        >
          {saving ? 'Saving…' : 'Apply'}
        </Button>
      </div>
      {/* The placeholder already carries the default, so an empty field needs
          no caption; only a bad number has something to say. */}
      {invalid && (
        <p className="text-[11px] text-[var(--co-error)]">Enter a percentage between 0 and 100.</p>
      )}
    </div>
  );
}
